import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { keyframes, styled } from "goober";

import { IS_DEV, IS_NODE } from "@/__main__/constants.mjs";
import router, { EVENT_LOADED_DATA } from "@/__main__/router.mjs";
import eventBus from "@/app/app-event-bus.mjs";
import { EVENT_CUSTOM_ERROR } from "@/app/constants.mjs";
import { EVENT_ERROR } from "@/app/ErrorBoundary.jsx";
import CloseIcon from "@/inline-assets/close-icon.svg";
import { classNames } from "@/util/class-names.mjs";
import { NotFoundError } from "@/util/custom-errors.mjs";
import sourceMap from "@/util/error-sourcemap.mjs";
import isRouteOverlay from "@/util/is-route-overlay.mjs";
import { stringifyPath } from "@/util/path-regexp.mjs";
import { useRoute } from "@/util/router-hooks.mjs";

const TIME_MS = IS_NODE ? 0 : 8 * 1000;

const anim1 = () => keyframes`
  0% {
    transform: scaleX(1);
  }
  100% {
    transform: scaleX(0);
  }
`;

const anim2 = () => keyframes`
  0% {
    transform: scaleX(0);
  }
  100% {
    transform: scaleX(0);
  }
`;

const ErrorBlock = styled("pre")`
  background: var(--background, var(--shade8));
  border-radius: var(--br-sm);
  padding: var(--sp-2) var(--sp-3);
  overflow: auto;
  width: 100vw;
  max-width: 100%;
  margin: var(--sp-3) 0 var(--sp-1);
`;

const ErrorNotification = styled("div")`
  position: fixed;
  top: var(--sp-14);
  left: 50%;
  transform: translateX(-50%);
  z-index: 999;
  background: var(--shade10);
  box-shadow: 0 5px 10px var(--shade8);
  border-radius: var(--br-lg);
  padding: var(--sp-3) var(--sp-4);
  color: var(--shade2);
  max-width: calc(min(60ch, 100vw));
  align-items: center;
  overflow: hidden;
  & span {
    line-break: anywhere;
  }

  &.warning:not(.overlay),
  &.error:not(.overlay) {
    inset: var(--title-bar-height) 0 0 0;
    transform: none;
    max-width: 100%;
  }

  &.warning {
    background: color-mix(in srgb, transparent 12%, var(--yellow));
    color: var(--shade9);
    svg {
      fill: var(--shade9);
    }
  }

  &.error {
    background: color-mix(in srgb, transparent 12%, var(--red));
    color: var(--shade9);
    svg {
      fill: var(--shade9);
    }
  }

  svg {
    position: absolute;
    top: var(--sp-4);
    right: var(--sp-3);
    background: inherit;
    display: block;
    cursor: pointer;
    height: 1rem;
    transform: scale(1.5);
    fill: var(--shade4);
  }

  & > span {
    display: inline-block;
    &:last-of-type {
      padding-inline-end: var(--sp-7);
    }
  }
`;

const ShowError = styled("span")`
  color: var(--shade0);
  margin-inline-start: var(--sp-2);
  cursor: pointer;
`;

const CountDown = styled("div")`
  transform-origin: top left;
  animation: var(--animation, none);
  animation-fill-mode: forwards;
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: var(--cta-gradient);
`;

const RouteErrorNotification = () => {
  const { t } = useTranslation();
  const { currentPath } = useRoute();
  const timerRef = useRef(null);
  const [error, setError] = useState(null);
  const [stack, setStack] = useState(null);
  const [showError, setShowError] = useState(false);

  // Handling router event
  useEffect(() => {
    async function handleError(error) {
      if (
        checkShouldSuppress(error) ||
        !error ||
        error instanceof NotFoundError
      )
        return;

      setError(error);

      try {
        const [mappedOutput] = await sourceMap(error);
        setStack(mappedOutput);

        // For devs, this can cause infinite looping of error notifications.
        if (!IS_DEV) {
          eventBus.emit(EVENT_ERROR, {
            error,
            mappedOutput,
            tags: ["route_error_notification"],
            route: router?.route?.path
              ? stringifyPath(router?.route?.path)
              : "",
            routeParams: router?.route?.parameters,
          });
        }
      } catch (_e) {
        // Should actually just completely swallow this error.
        // devError("Failed to sourcemap from RouteErrorNotification!", e);
      }
    }

    const checkRouteForError = () => {
      if (router.error) {
        handleError(router.error);
      }
    };

    router.events.on(EVENT_LOADED_DATA, checkRouteForError);
    checkRouteForError(); // call w/o event first, to handle initial error

    const customErrorListener = (error) => {
      handleError(error);
    };

    eventBus.on(EVENT_CUSTOM_ERROR, customErrorListener);

    return () => {
      router.events.off(EVENT_LOADED_DATA, checkRouteForError);
      eventBus.off(EVENT_CUSTOM_ERROR, customErrorListener);
    };
  }, [setError]);

  // Automatic timeout
  useEffect(() => {
    if (!error || error.isInternal) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
      return;
    }
    timerRef.current = setTimeout(() => {
      setError(null);
      setStack(null);
    }, TIME_MS);
    return () => {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    };
  }, [error, setError]);

  const close = useCallback(() => {
    setError(null);
    setStack(null);
  }, []);

  const showHandler = useCallback(() => {
    clearTimeout(timerRef.current);
    timerRef.current = null;
    setShowError(true);
  }, []);

  if (!error || shouldHideErrorNotification()) return null;

  let errorInput = error;
  if (typeof error === "object" && !(error instanceof Error)) {
    errorInput = JSON.stringify(error);
  }

  const stackFragment = (type?: string) =>
    stack ? (
      <ErrorBlock
        style={{
          "--background":
            type === "dev" ? "hsla(var(--shade0-hsl) / 0.25)" : "var(--shade8)",
        }}
      >
        {Array.isArray(stack) ? stack.join("\n") : stack}
      </ErrorBlock>
    ) : null;
  const showBlock = error.isInternal ? (
    stackFragment("dev")
  ) : showError ? (
    <>
      <ErrorBlock>{`${errorInput}`}</ErrorBlock>
      {stackFragment()}
    </>
  ) : (
    <ShowError className="type-body2" onClick={showHandler}>
      {t("common:showMore", "Show more")}
    </ShowError>
  );

  return (
    <ErrorNotification
      {...classNames(
        error.internalClass,
        isRouteOverlay(currentPath) && "overlay",
      )}
    >
      <CountDown
        style={{
          "--animation": `${
            timerRef.current ? anim1() : anim2()
          } ${TIME_MS}ms linear`,
        }}
      />
      <span className="type-body2">
        {error.isInternal || error.isPublic
          ? error.message
          : t("common:error.serverError", "Server error. Check our Discord.")}
      </span>

      {showBlock}
      <CloseIcon onClick={close} />
    </ErrorNotification>
  );
};

function checkShouldSuppress(error) {
  return (
    /(?:^https?:\/\/riot\.iesdev\.com|DivisionStats)/.test(error?.url) ||
    /^dev server/i.test(error?.name)
  );
}

function shouldHideErrorNotification() {
  return !IS_DEV && isRouteOverlay(router.route?.currentPath);
}

export default RouteErrorNotification;
